/**
 * ============================================================
 * AdminUserForm.js — Create/Edit Admin User
 * ============================================================
 *
 * Modes:
 *   - NEW:  /admin/users/new
 *   - EDIT: /admin/users/edit/:id
 * 
 * Permissions:
 *   ROOT ADMIN (username="admin"):
 *     - Create users
 *     - Edit any user
 *     - Delete any user except themselves
 *     - Cannot rename themselves ("admin" username locked)
 *
 *   NON-ROOT ADMINS:
 *     - Create users
 *     - Edit only their own username/password
 *     - Cannot edit root admin
 *     - Cannot edit other admins
 *     - Cannot delete anyone
 *
 * Notes:
 *   - Uses authFetch() for auto-redirect on token expiry
 **/

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import SynlabAdminNavbar from "../components/SynlabAdminNavbar";
import SynlabAdminFooter from "../components/SynlabAdminFooter";

import { authFetch } from "../../utils/authFetch";

import "./css/AdminSubpage.css";
import "./css/AdminForm.css";


// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function AdminUserForm() {
  const navigate = useNavigate();

  // Editing mode is determined by presence of :id in the URL
  const { id } = useParams();
  const editing = id !== undefined && id !== "new";

  // Form fields
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // Auth / permissions
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUsername, setCurrentUsername] = useState(null);
  const isRootAdmin = currentUsername === "admin";

  // The user being edited (when editing)
  const [targetUser, setTargetUser] = useState(null);

  // ------------------------------------------------------------
  // Validate token + extract logged-in user ID
  // ------------------------------------------------------------
  useEffect(() => {
    async function validate() {
      const res = await authFetch("/api/authenticate");
      if (!res) return;

      // Decode user id from JWT
      const token = localStorage.getItem("token");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setCurrentUserId(payload.id);
      }
    }

    validate();
  }, []);

  // ------------------------------------------------------------
  // Load logged-in user info (needed to know username)
  // ------------------------------------------------------------
  useEffect(() => {
    async function loadCurrent() {
      const res = await authFetch("/api/admin/me");
      if (!res) return;

      const data = await res.json();
      setCurrentUsername(data.username);
    }
    loadCurrent();
  }, []);

  // ------------------------------------------------------------
  // Load target user when editing
  // ------------------------------------------------------------
  useEffect(() => {
    if (!editing) return;

    async function loadUser() {
      const res = await authFetch("/api/admin/users");
      if (!res) return;

      const allUsers = await res.json();
      const u = allUsers.find((x) => x.id === Number(id));

      if (!u) {
        alert("User not found.");
        return navigate("/admin/users");
      }

      setTargetUser(u);
      setUsername(u.username);
    }

    loadUser();
  }, [editing, id, navigate]);

  // ------------------------------------------------------------
  // Permissions helpers
  // ------------------------------------------------------------
 
  // Non-root admin can only edit themselves
  function canEditTargetUser() {
    if (!editing) return true; // new user
    if (isRootAdmin) return true;
    return targetUser?.id === currentUserId;
  }

  // Root admin cannot rename themselves
  function usernameDisabled() {
    if (!editing) return false;
    if (isRootAdmin && targetUser?.username === "admin") return true;
    if (!isRootAdmin && targetUser?.id !== currentUserId) return true;
    return false;
  }

  // Only root or self can change password
  function passwordDisabled() {
    if (!editing) return false;
    if (isRootAdmin) return false;
    return targetUser?.id !== currentUserId;
  }

  // Delete button rules
  function canDeleteTargetUser() {
    if (!editing) return false;

    // Only root admin may delete
    if (!isRootAdmin) return false;

    // Cannot delete themselves
    if (targetUser?.id === currentUserId) return false;

    // Cannot delete root admin
    if (targetUser?.username === "admin") return false;

    return true;
  }

  // Save button rules  
  function showSaveButton() {

    // always show for new user
    if (!editing) return true;

    // root admin can always save
    if (isRootAdmin) return true;

    // others only for themselves
    return targetUser?.id === currentUserId;
  }


  // ------------------------------------------------------------
  // Submit form — create or update admin user
  // ------------------------------------------------------------
  async function handleSubmit(e) {
    e.preventDefault();

    // Check permissions again
    if (!canEditTargetUser()) {
      alert("You do not have permission to edit this user.");
      return;
    }

    // Build request body dynamically
    const body = { username };
    if (password) body.password = password;  // Only send if changed/new

    // Editing or creating
    const method = editing ? "PUT" : "POST";
    const endpoint = editing
      ? `/api/admin/users/${id}`
      : `/api/admin/users`;

    const res = await authFetch(endpoint, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!res) return; // authFetch handled redirect

    // Go back to admin user list
    navigate("/admin/users");
  }

  // ------------------------------------------------------------
  // Delete user
  // ------------------------------------------------------------
  async function handleDelete() {
    if (!canDeleteTargetUser()) return;

    // Check before deleting
    if (!window.confirm("Delete this admin user?")) return;

    const res = await authFetch(`/api/admin/users/${id}`, {
      method: "DELETE",
    });

    if (!res) return;

    // Go back to admin user list
    navigate("/admin/users");
  }


  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div className="admin-wrapper">

      <SynlabAdminNavbar />

      <div className="admin-subpage-container">

        {/* ----- PAGE TITLE ----- */}
        <div className="admin-page-header">
          <h1 className="admin-page-title">
            {editing ? "Edit Admin User" : "Create Admin User"}
          </h1>
        </div>

        {/* ----- PAGE DESCRIPTION ----- */}
        <p className="admin-page-description">
          {editing
            ? "Modify this admin user's credentials."
            : "Create a new admin user with access to the Synlab backend."}
        </p>

        {/* ----- FORM ----- */}
        <form onSubmit={handleSubmit} className="admin-form">

          {/* ----- USERNAME FIELD ----- */}
          <div className="mb-3">
            <label className="form-label">Username</label>
            <input
              className="form-control"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={usernameDisabled()}
              required
            />
          </div>

          {/* ----- PASSWORD FIELD ----- */}
          <div className="mb-3">
            <label className="form-label">
              {editing ? "New Password (optional)" : "Password"}
            </label>
            <input
              type="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={passwordDisabled()}
              placeholder={editing ? "Leave blank to keep current password" : ""}
            />
          </div>

          {/* ----- SUBMIT + CANCEL BUTTONS ----- */}
          <div className="admin-form-buttons">
            {showSaveButton() && (
              <button className="btn btn-primary" type="submit">
                {editing ? "Save Changes" : "Create User"}
              </button>
            )}

            {canDeleteTargetUser() && (
              <button
                className="btn btn-danger"
                type="button"
                onClick={handleDelete}
              >
                Delete User
              </button>
            )}

            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => navigate("/admin/users")}
            >
              Cancel
            </button>
          </div>

        </form>
      </div>

      {/* ----- FOOTER ----- */}
      <SynlabAdminFooter />
    </div>
  );
}

export default AdminUserForm;
